/**
 * Data Masking Utility
 * Masks sensitive data to prevent exposure in logs, error messages, and UI
 */

/**
 * Patterns for detecting sensitive data
 */
const SENSITIVE_PATTERNS = {
  // Email addresses
  email: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
  
  // Phone numbers (various formats)
  phone: /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g,
  
  // Social Security Numbers (US format)
  ssn: /\b\d{3}-\d{2}-\d{4}\b/g,
  
  // Credit card numbers
  creditCard: /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g,
  
  // UUIDs (may contain sensitive identifiers)
  uuid: /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
  
  // API keys (common patterns)
  apiKey: /\b(api[_-]?key|apikey|access[_-]?token|secret[_-]?key)\s*[:=]\s*['"]?([a-zA-Z0-9_\-]{20,})['"]?/gi,
  
  // Passwords (in error messages or logs)
  password: /(password|passwd|pwd)\s*[:=]\s*['"]?([^'"\s]{3,})['"]?/gi,
  
  // JWT tokens
  jwt: /\beyJ[A-Za-z0-9-_=]+\.eyJ[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*\b/g,
  
  // IP addresses (may be sensitive in some contexts)
  ipAddress: /\b(?:\d{1,3}\.){3}\d{1,3}\b/g,
  
  // Dates of birth (various formats)
  dateOfBirth: /\b\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\b/g,
  
  // Medical record numbers
  medicalRecord: /\b(MRN|MR|Record)[\s#:]*\d{4,}\b/gi,
  
  // Health insurance numbers
  insurance: /\b(Insurance|Policy|Member)[\s#:]*\d{4,}\b/gi,
}

/**
 * Mask sensitive data in string
 */
export const maskSensitiveData = (input, options = {}) => {
  if (input === null || input === undefined) {
    return input
  }

  if (typeof input !== 'string') {
    // For objects, recursively mask
    if (typeof input === 'object') {
      return maskObject(input, options)
    }
    return input
  }

  let masked = input
  const {
    maskChar = '*',
    showFirst = 0,
    showLast = 0,
    patterns = Object.keys(SENSITIVE_PATTERNS),
    customPatterns = [],
  } = options

  // Mask email addresses
  if (patterns.includes('email') || patterns.includes('all')) {
    masked = masked.replace(SENSITIVE_PATTERNS.email, (match) => {
      const [local, domain] = match.split('@')
      const maskedLocal = maskString(local, { maskChar, showFirst: 2, showLast: 0 })
      return `${maskedLocal}@${domain}`
    })
  }

  // Mask phone numbers
  if (patterns.includes('phone') || patterns.includes('all')) {
    masked = masked.replace(SENSITIVE_PATTERNS.phone, (match) => {
      return maskString(match.replace(/\D/g, ''), { maskChar, showFirst: 0, showLast: 4 })
    })
  }

  // Mask SSN
  if (patterns.includes('ssn') || patterns.includes('all')) {
    masked = masked.replace(SENSITIVE_PATTERNS.ssn, (match) => {
      return `***-**-${match.slice(-4)}`
    })
  }

  // Mask credit card numbers
  if (patterns.includes('creditCard') || patterns.includes('all')) {
    masked = masked.replace(SENSITIVE_PATTERNS.creditCard, (match) => {
      const digits = match.replace(/\D/g, '')
      return `****-****-****-${digits.slice(-4)}`
    })
  }

  // Mask UUIDs (optional, may be needed for some contexts)
  if (patterns.includes('uuid') || patterns.includes('all')) {
    masked = masked.replace(SENSITIVE_PATTERNS.uuid, (match) => {
      return maskString(match, { maskChar, showFirst: 8, showLast: 4 })
    })
  }

  // Mask API keys
  if (patterns.includes('apiKey') || patterns.includes('all')) {
    masked = masked.replace(SENSITIVE_PATTERNS.apiKey, (match, key, value) => {
      return `${key}: ${maskString(value, { maskChar, showFirst: 4, showLast: 4 })}`
    })
  }

  // Mask passwords
  if (patterns.includes('password') || patterns.includes('all')) {
    masked = masked.replace(SENSITIVE_PATTERNS.password, (match, key, value) => {
      return `${key}: ${maskChar.repeat(Math.min(value.length, 8))}`
    })
  }

  // Mask JWT tokens
  if (patterns.includes('jwt') || patterns.includes('all')) {
    masked = masked.replace(SENSITIVE_PATTERNS.jwt, (match) => {
      return maskString(match, { maskChar, showFirst: 10, showLast: 0 })
    })
  }

  // Mask IP addresses (optional)
  if (patterns.includes('ipAddress') || patterns.includes('all')) {
    masked = masked.replace(SENSITIVE_PATTERNS.ipAddress, (match) => {
      const parts = match.split('.')
      return `${parts[0]}.${parts[1]}.${maskChar.repeat(parts[2].length)}.${parts[3]}`
    })
  }

  // Mask dates of birth
  if (patterns.includes('dateOfBirth') || patterns.includes('all')) {
    masked = masked.replace(SENSITIVE_PATTERNS.dateOfBirth, (match) => {
      return `**/**/****`
    })
  }

  // Mask medical record numbers
  if (patterns.includes('medicalRecord') || patterns.includes('all')) {
    masked = masked.replace(SENSITIVE_PATTERNS.medicalRecord, (match) => {
      return match.replace(/\d/g, maskChar)
    })
  }

  // Mask insurance numbers
  if (patterns.includes('insurance') || patterns.includes('all')) {
    masked = masked.replace(SENSITIVE_PATTERNS.insurance, (match) => {
      return match.replace(/\d/g, maskChar)
    })
  }

  // Apply custom patterns
  customPatterns.forEach((pattern) => {
    if (pattern.regex && pattern.mask) {
      masked = masked.replace(pattern.regex, pattern.mask)
    }
  })

  return masked
}

/**
 * Mask a string with configurable visibility
 */
const maskString = (str, options = {}) => {
  const { maskChar = '*', showFirst = 0, showLast = 0 } = options

  if (str.length <= showFirst + showLast) {
    return maskChar.repeat(str.length)
  }

  const first = str.slice(0, showFirst)
  const last = showLast > 0 ? str.slice(-showLast) : ''
  const middle = maskChar.repeat(Math.max(str.length - showFirst - showLast, 4))

  return `${first}${middle}${last}`
}

/**
 * Mask sensitive data in object recursively
 */
export const maskObject = (obj, options = {}) => {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => maskObject(item, options))
  }

  if (typeof obj === 'string') {
    return maskSensitiveData(obj, options)
  }

  if (typeof obj !== 'object') {
    return obj
  }

  const masked = {}
  const sensitiveKeys = [
    'password',
    'passwd',
    'pwd',
    'secret',
    'token',
    'api_key',
    'apikey',
    'access_token',
    'refresh_token',
    'ssn',
    'social_security',
    'credit_card',
    'card_number',
    'cvv',
    'cvc',
    'email',
    'phone',
    'phone_number',
    'date_of_birth',
    'dob',
    'medical_record',
    'insurance_number',
    'ip_address',
    'ip',
  ]

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const keyLower = key.toLowerCase()
      const value = obj[key]

      // Check if key indicates sensitive data
      if (sensitiveKeys.some((sk) => keyLower.includes(sk))) {
        if (typeof value === 'string') {
          masked[key] = maskSensitiveData(value, options)
        } else {
          masked[key] = '[REDACTED]'
        }
      } else if (typeof value === 'object') {
        masked[key] = maskObject(value, options)
      } else if (typeof value === 'string') {
        // Check if value contains sensitive patterns
        masked[key] = maskSensitiveData(value, options)
      } else {
        masked[key] = value
      }
    }
  }

  return masked
}

/**
 * Detect if string contains sensitive data
 */
export const detectSensitiveData = (input) => {
  if (typeof input !== 'string') {
    return false
  }

  return Object.values(SENSITIVE_PATTERNS).some((pattern) => pattern.test(input))
}

/**
 * Get list of detected sensitive data types
 */
export const getSensitiveDataTypes = (input) => {
  if (typeof input !== 'string') {
    return []
  }

  const detected = []

  for (const [type, pattern] of Object.entries(SENSITIVE_PATTERNS)) {
    if (pattern.test(input)) {
      detected.push(type)
    }
  }

  return detected
}

/**
 * Redact sensitive data completely
 */
export const redactSensitiveData = (input, replacement = '[REDACTED]') => {
  if (typeof input !== 'string') {
    return input
  }

  let redacted = input

  Object.values(SENSITIVE_PATTERNS).forEach((pattern) => {
    redacted = redacted.replace(pattern, replacement)
  })

  return redacted
}

/**
 * Mask error message
 */
export const maskErrorMessage = (error, options = {}) => {
  if (!error) {
    return 'An error occurred'
  }

  const {
    showStack = false,
    showDetails = false,
    maskUserData = true,
    customMessage = null,
  } = options

  // Use custom message if provided
  if (customMessage) {
    return customMessage
  }

  // Get error message
  let message = error.message || error.toString() || 'An error occurred'

  // Mask sensitive data in message
  if (maskUserData) {
    message = maskSensitiveData(message, {
      patterns: ['all'],
      showFirst: 0,
      showLast: 0,
    })
  }

  // Remove stack traces unless explicitly allowed
  if (!showStack && error.stack) {
    message = message.split('\n')[0] // Only first line
  }

  // Remove sensitive details
  if (!showDetails) {
    // Remove file paths
    message = message.replace(/\/[^\s]+/g, '[path]')
    // Remove line numbers
    message = message.replace(/:\d+:\d+/g, ':[line]')
  }

  return message
}

export default {
  maskSensitiveData,
  maskObject,
  detectSensitiveData,
  getSensitiveDataTypes,
  redactSensitiveData,
  maskErrorMessage,
}

